import { Compass, Crown, LogOut } from "lucide-react";
import { AccountSubscription, type SubStatus } from "./AccountSubscription";
import { EmailSettings } from "./EmailSettings";
import { LanguageChoice } from "./LanguageChoice";
import { MenuIcon } from "./MenuIcon";
import { DailyLineSetting } from "./DailyLineSetting";
import { JournalLalaSetting } from "./JournalLalaSetting";
import { PushSettings } from "./PushSettings";
import { SmsSettings } from "./SmsSettings";
import { InstallReoffer } from "./InstallReoffer";
import { t } from "../strings.js";
// ITEM G3 — the store app offers no upgrade and no billing door (R3: payment stays on the website).
import { APP_MODE } from "../lib/appMode";
import { DeleteAccount } from "../storeApp/DeleteAccount";

// ITEM 14(c) / roadmap 7 — **THE PANEL BEHIND THE MEMBER PILL, WHICH IS THE ONE ACCOUNT CONTROL ON
// EVERY PAGE OF THIS SITE**, and until now it was sixty lines of JSX inside a 13,000-line file with
// nothing but regexes over it (`t1-gating-fixtures` matched a ternary as a string; `r22-mobile-nav`
// sliced the file between two class names to find the owner's door).
//
// It came out because ITEM 14 needed somewhere to put the language switcher that a member can reach
// from any page without entering My Room — and because a strings layer with no call site is R38's
// trap exactly: a capability wired to nothing has no error state. Props in, tree out, no hook, so
// `scripts/item14-strings.mjs` CALLS it once per language and reads the tree back. The two gates that
// were reading it as source text are MIGRATED onto calling it, which is the upgrade an extraction
// buys (R9): presence becomes behaviour.
//
// **IN ENGLISH THE TREE IS BYTE-IDENTICAL TO WHAT IT REPLACED.** `language` defaults to "en" and
// every class name is unchanged, which is what keeps the four rendered browser checks that point at
// `.account-menu__panel` (au1, t1-members-menu, t8-membership-state, w1-upgrade) pointing at the same
// thing. The comments below are the ones that travelled with the code, because they are the reasons.
export function AccountMenuPanel({
  email, isMember, isOwner, serverTier, subStatus, billingBusy = false, billingNote = "",
  subNote = "",
  language = "en", onGoTo, onOpenBilling, onLogOut, onChooseLanguage, onOpenInstructions, onOpenMemory,
}: {
  email: string;
  isMember: boolean;
  isOwner: boolean;
  // NULL is a real state and it is not "free": it is her tier before the session answers. The
  // original ternary read it as not-vet and offered the upgrade entry, and that behaviour travelled
  // with the code rather than being tidied into a default here.
  serverTier: string | null;
  subStatus: SubStatus | null;
  billingBusy?: boolean;
  billingNote?: string;
  // R87: what the MEMBERSHIP read said when it did not land. Deliberately NOT `billingNote` — that
  // one is the portal's own word about opening Stripe, a different fact about a different act, and
  // one state doing two jobs is R66's collision.
  subNote?: string;
  language?: string;
  // "contact" joined for the undeliverable prompt: it is the only door that can really move a member
  // off a dead address today, since this site has no email-change control (see the note below).
  onGoTo: (page: "my-room" | "membership" | "dashboard" | "contact") => void;
  onOpenInstructions: () => void;
  // MEM-1, her clause 2: the clear-all lives in account settings, so the door to it does too.
  onOpenMemory: () => void;
  onOpenBilling: () => void;
  onLogOut: () => void;
  onChooseLanguage: (language: string) => void;
}) {
  return (
    <div className="account-menu__panel" role="menu">
      <p className="account-menu__who">
        <span>{t(language, "account.signedInAs")}</span>
        <strong>{email}</strong>
      </p>
      <button type="button" className="account-menu__item" role="menuitem" onClick={() => onGoTo("my-room")}>
        {t(language, "account.myRoom")}
      </button>
      {/* HER MENU LINK (her words, 2026-10-08): "Twelve house members need a truth link in their menu to be able
          to go directly over if they are signed in." It goes through /api/truth-door, the one-time sign-in handoff, so
          she lands on Truth signed in as the same person; Truth's own "Visit 12th House" brings her back. Not in the
          store app: Truth sells readings, and payment stays on the website (R3). */}
      {!APP_MODE ? (
        <a className="account-menu__item" role="menuitem" href="/api/truth-door?to=%2F">
          {t(language, "account.truth")}
        </a>
      ) : null}
      {/* T1 — SUBSCRIPTION. What she is on, said plainly, and what happens next. */}
      {/* D2-1: the whole block — including the gift-truth state a comped member sees — is in
          src/components/AccountSubscription.tsx, where a gate CALLS it per state. */}
      {/* >>> **HER CLAUSE 3 (2026-08-14): THE MEMBERS THIS HOUSE CANNOT REACH ARE TOLD, WARMLY.**
          <<< Fifteen carry `email_undeliverable_at`. Fourteen typed one character wrong at a signup
          field that had no `type="email"` on it, and this site has never mentioned it — they are
          simply missing their readings, their reminders and everything Lala sends.

          **IT SITS ABOVE THE SUBSCRIPTION BLOCK ON R87's OWN REASONING:** a member reading her
          account menu is here to find out what is going on with her account, and *we cannot reach
          you* outranks *what tier you are on*.

          **>>> AND THE DOOR HER SENTENCE ASSUMED EXISTS NOW — THIS ENTRY IS MIGRATED AT ITS
          DECLARATION RATHER THAN LEFT STANDING BESIDE ITS REPLACEMENT (DL-2/DT-2b/R181). <<<** It
          used to read *"there is NO email-change control on this site… a prompt whose action does
          nothing is A2's dead box, so it opens the contact form… The missing control is its own
          item."* That item shipped. **The prompt moved INTO `EmailSettings` with the field it was
          already telling her about** — its own copy ends *"Check the spelling below and save it"*,
          and until now there was nothing below it — so the two are one surface, the word *below* is
          literally true, and the panel that knows whether she CAN change it is the one that decides
          whether the contact button is still the only door she has (R18).

          **IT KEEPS THE PROMPT'S POSITION AND THAT IS R87's OWN RULING, UNTOUCHED:** above the
          subscription block, because *we cannot reach you* outranks *what tier you are on*. */}
      <EmailSettings
        undeliverable={subStatus?.emailUndeliverable === true}
        onContact={() => onGoTo("contact")}
      />

      <AccountSubscription status={subStatus} isMember={isMember} language={language} />

      {/* R87 — **IT STANDS HERE, AND THE POSITION IS THE WHOLE RULING.** A failed
          /api/membership/status read distorts THREE controls on this panel and they are not
          adjacent: the tier line ABOVE it says "Free" (that fallback is reachable on no other
          condition, because a landed read always carries `tierLabel`), the upgrade row BELOW it
          offers a membership she may already hold, and the billing row below THAT is not rendered at
          all. One sentence between the first and the second covers the lot; anywhere lower and it is
          under the row it is correcting. */}
      {subNote ? <p className="account-menu__note account-menu__note--warn">{subNote}</p> : null}

      {/* T8-8 — THE DROPDOWN SELLS THE NEXT STEP. "See what is open" told a member nothing about what
          to do, and it was the same line whatever tier she was on. Below Vet the entry is UPGRADE and
          it opens the membership page in its know-who-she-is state (T8-7); a Vet is already at the top
          and is offered nothing, because there is nothing above her to sell. A member on no tier at
          all is joining rather than upgrading, so she is asked to choose. */}
      {!APP_MODE && serverTier !== "vet" ? (
        <button
          type="button"
          className={isMember ? "account-menu__item account-menu__item--upgrade" : "account-menu__item"}
          role="menuitem"
          onClick={() => onGoTo("membership")}
        >
          {isMember ? <><Crown size={15} /> {t(language, "account.upgrade")}</> : t(language, "account.chooseMembership")}
        </button>
      ) : null}

      {/* ITEM 3 — **SITE INSTRUCTIONS MOVED OFF THE TOP BAR AND INTO THIS MENU** (her order): on a
          phone the top right was carrying four things and she wanted three. It is one entry here,
          in the same list as everything else about her account, and the panel does not decide what
          the guide IS — it asks the page to open it, so there is still one guide and one opener. */}
      <button type="button" className="account-menu__item" role="menuitem" onClick={() => onOpenInstructions()}>
        <Compass size={15} /> {t(language, "nav.siteInstructions")}
      </button>

      {/* MEM-1 (her order, 2026-09-01) — **HER CLAUSE 2's CLEAR-ALL LIVES IN ACCOUNT SETTINGS**, so
          the door to it is one entry here beside everything else about her account. The panel does not
          decide what the memory IS: it asks the page to open it, the same split the guide entry above
          ships on, so there is one memory and one opener. */}
      <button type="button" className="account-menu__item" role="menuitem" onClick={() => onOpenMemory()}>
        <Compass size={15} /> What Lala remembers
      </button>

      {/* Only shown when there is genuinely something to manage. Offering "manage billing" to a member
          who has never paid is a dead end wearing a button. */}
      {!APP_MODE && subStatus?.canManageBilling ? (
        <button type="button" className="account-menu__item" role="menuitem"
          onClick={() => onOpenBilling()} disabled={billingBusy}>
          {billingBusy ? t(language, "account.openingBilling") : t(language, "account.manageBilling")}
        </button>
      ) : null}
      {/* P2 (her item 9 choice, "YES, GENTLY"): only for a member on Link, debit or prepaid with no second card saved. It
          opens the same Stripe billing page, where she adds a card; a failed renewal then tries it once. */}
      {!APP_MODE && subStatus?.canManageBilling && subStatus?.backupCardSuggested ? (
        <>
          <p className="account-menu__note">{t(language, "account.backupCardNote")}</p>
          <button type="button" className="account-menu__item" role="menuitem" onClick={() => onOpenBilling()} disabled={billingBusy}>
            {t(language, "account.addBackupCard")}
          </button>
        </>
      ) : null}
      {billingNote ? <p className="account-menu__note account-menu__note--warn">{billingNote}</p> : null}

      {/* W14-3 — THE OWNER'S DASHBOARD DOOR, AND ON A PHONE IT IS THE ONLY ONE. B3 put her door in the
          sidebar; `body.m3-shell .site-sidebar { display: none }` hides that whole sidebar on the
          mobile shell, so signed in as owner on her phone there was NO visible way to the dashboard at
          all — only a typed /dashboard. It lives here because this menu is where account actions
          already live (AU1-2) and it is the one header control that survives on every viewport.
          Convenience, never the gate: every dashboard endpoint checks the role server-side regardless
          of what any client chooses to render. */}
      {isOwner ? (
        <button type="button" className="account-menu__item" role="menuitem" onClick={() => onGoTo("dashboard")}>
          <MenuIcon page="dashboard" /> {t(language, "account.dashboard")}
        </button>
      ) : null}

      {/* P4-3/P4-4 — **THE NOTIFICATIONS DOOR, AND THIS MENU ENTRY IS THE ONLY ONE THERE HAS EVER
          BEEN.** Her ruling is that the menu entry is THE door and the My Room bell retires; measured
          (R25), there was no bell — no manual push control existed anywhere in `src/`, so a member
          who tapped "Not now" on the one-shot sheet could never be asked again and had nowhere to
          go. It lives here for the switcher's own reason, one preference over: this menu is on every
          page, so it exists on desktop and in the installed app alike without a roster of surfaces
          having to remember it.

          It holds hooks and comes anyway, on R10-3's ruling: the gates' stub jsx-runtime never CALLS
          a child, so `<PushSettings/>` is `{ type, props }` here and this panel stays fully callable
          by `scripts/item14-strings.mjs`. Its own hookless view is CALLED by `p4-push-settings.mjs`. */}
      <PushSettings language={language} />

      {/* IT #179 — **THE MORNING LINE'S OWN SWITCH, DIRECTLY UNDER THE NOTIFICATIONS DOOR AND NOT
          INSIDE IT.** sshinetha asked to stop the daily guidance because the lines kept speaking to
          a home and a family she does not have, and the only exit this house had was the control
          above this one — which would also have stopped her hearing that someone messaged her. It
          sits HERE for its neighbour's own reason: this menu is on every page, so the door exists
          on desktop and in the installed app alike without a roster of surfaces having to remember
          it. And it sits BELOW notifications rather than above, because the wider switch is the one
          a member comes looking for and the narrower one is the answer she did not know to ask for.

          It holds hooks and comes anyway, on R10-3's ruling: the gates' stub jsx-runtime never
          CALLS a child, so `<DailyLineSetting/>` is `{ type, props }` here and this panel stays
          fully callable. Its own hookless view is CALLED by `scripts/it179-daily-line-setting.mjs`. */}
      <DailyLineSetting language={language} />
      {/* LALA-BRAIN-1 clause 2: her journal control sits beside the morning line because they are
          the same KIND of decision — what this house may do with her, one feature at a time — and a
          member looking for the one will find the other. */}
      <JournalLalaSetting language={language} />

      {/* P3 — **THE STANDING SMS DOOR, AND IT LIVES HERE FOR ITS NEIGHBOUR'S OWN REASON.** Her
          amendment is that *declining is never final*: P2's popup asks once and either answer ends
          it forever, so without a permanent door a member who pressed "Not now" could never give
          this house her number — which is P4's own finding one preference over, where "Not now" on
          the push sheet was permanent and there was nowhere to go. This menu is on every page, so
          the door exists on desktop and in the installed app alike without a roster of surfaces
          having to remember it.

          It holds hooks and comes anyway, on R10-3's ruling: the gates' stub jsx-runtime never
          CALLS a child, so `<SmsSettings/>` is `{ type, props }` here and this panel stays fully
          callable. Its own hookless view is CALLED by `p3-sms-settings.mjs`. */}
      <SmsSettings language={language} />

      {/* INSTALL-1 CLAUSE 7 — **THE WAY BACK, AND IT LIVES HERE BY HER OWN WORD.** Her MENU SIZE
          order names the entries this menu keeps: *"Have an issue?, SMS Notifications, the
          re-offer button"*. It is beside its two neighbours for their own recorded reason — this
          menu is on every page, so the door exists on desktop and in the installed app alike
          without a roster of surfaces having to remember it. Its three states are RUN by
          `scripts/install-reoffer.mjs` and its hookless view is CALLED there. */}
      <InstallReoffer language={language} />

      {/* ITEM 14(c) — THE SWITCHER LIVES HERE because this menu is on every page. My Room's settings
          hold the theme and this too, but a member who cannot read the house well enough to find My
          Room must not have to. */}
      <LanguageChoice language={language} onChoose={onChooseLanguage} />

      <button type="button" className="account-menu__item account-menu__item--out" role="menuitem" onClick={() => onLogOut()}>
        <LogOut size={15} /> {t(language, "account.logOut")}
      </button>
      {/* ITEM G6 — Apple 5.1.1(v): the store app lets her delete her account from inside it. */}
      {APP_MODE ? <DeleteAccount /> : null}
    </div>
  );
}
